import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PushNotificationToggle from '../components/PushNotificationToggle';
import GuestOnboarding from '../components/GuestOnboarding';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, MapPin, Gift, ClipboardCheck, ArrowRight, Star, Copy, Bell, Settings, Key, Check, BookOpen, AlertCircle, Clock, Phone, Shield } from 'lucide-react';

import { API } from '../lib/api';

export default function DashboardPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codiceIngresso, setCodiceIngresso] = useState(null);
  const [activeBooking, setActiveBooking] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [codeUnavailableReason, setCodeUnavailableReason] = useState(null);
  const [hasActiveCheckin, setHasActiveCheckin] = useState(false);
  
  // New state for unit info (Phase A)
  const [unitName, setUnitName] = useState(null);
  const [unitNameEn, setUnitNameEn] = useState(null);
  const [validityStart, setValidityStart] = useState(null);
  const [validityEnd, setValidityEnd] = useState(null);
  
  // Onboarding state (Phase B)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (token) {
      fetchCheckins();
      fetchActiveCheckin();
      checkOnboardingStatus();
    }
  }, [token]);
  
  // Check if user needs onboarding (email verification)
  const checkOnboardingStatus = async () => {
    // Skip if already dismissed this session
    if (onboardingDismissed) return;
    
    try {
      const response = await axios.get(`${API}/guest-account/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOnboardingData(response.data);
      
      // Show onboarding if needed and not already verified
      if (response.data.show_onboarding && !response.data.email_verified) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // Don't show onboarding on error - fail gracefully
    }
  };
  
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    toast.success(language === 'en' ? 'Email verified!' : 'Email verificata!');
  };
  
  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    setOnboardingDismissed(true);
    // Don't show again this session
  };

  const fetchActiveCheckin = async () => {
    try {
      const response = await axios.get(`${API}/checkin/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasActiveCheckin(response.data.has_active_checkin);
      
      if (response.data.has_active_checkin) {
        // Set entry code
        setCodiceIngresso(response.data.codice_ingresso);
        setActiveBooking(response.data.checkin || response.data.booking);
        
        // Set unit info (new fields from Phase A)
        setUnitName(response.data.unit_name);
        setUnitNameEn(response.data.unit_name_en);
        setValidityStart(response.data.validity_start);
        setValidityEnd(response.data.validity_end);
        
        // Determine reason if code is unavailable
        if (!response.data.codice_ingresso) {
          const checkin = response.data.checkin;
          const booking = response.data.booking;
          if (checkin?.status === 'pending' || booking?.status === 'pending') {
            setCodeUnavailableReason('checkin_pending');
          } else if (!response.data.unit_id) {
            setCodeUnavailableReason('unit_not_assigned');
          } else {
            setCodeUnavailableReason('preparing');
          }
        } else {
          setCodeUnavailableReason(null);
        }
      } else {
        // No active booking - reset state
        setCodiceIngresso(null);
        setActiveBooking(null);
        setUnitName(null);
        setUnitNameEn(null);
        setValidityStart(null);
        setValidityEnd(null);
        setCodeUnavailableReason('no_booking');
      }
    } catch (error) {
      console.error('Error fetching active checkin:', error);
      setCodeUnavailableReason('error');
    }
  };

  const fetchCheckins = async () => {
    try {
      const response = await axios.get(`${API}/checkin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckins(response.data);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    // Use active booking code if available, otherwise user's code
    const codeToCopy = activeBooking?.codice_prenotazione || user?.codice_prenotazione;
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      setCodeCopied(true);
      toast.success('Codice copiato negli appunti!');
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: 'status-pending', label: 'In attesa' },
      confirmed: { class: 'status-confirmed', label: 'Confermato' },
      completed: { class: 'status-completed', label: 'Completato' },
      cancelled: { class: 'status-cancelled', label: 'Annullato' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
      <span className={`${s.class} px-3 py-1 rounded-full text-xs font-medium`}>
        {s.label}
      </span>
    );
  };

  if (authLoading || !user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }
  
  // Show onboarding overlay if needed
  if (showOnboarding && onboardingData) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Mini header */}
          <div className="mb-8 text-center">
            <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-2">
              {language === 'en' ? 'Welcome' : 'Benvenuto'}
            </p>
            <h1 className="font-cinzel text-3xl text-[#1A202C] mb-2">
              {language === 'en' ? 'Hello' : 'Ciao'}, {user.nome}!
            </h1>
          </div>
          
          {/* Onboarding Component */}
          <GuestOnboarding
            token={token}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
            suggestedEmail={onboardingData.suggested_email}
            suggestedEmailMasked={onboardingData.suggested_email_masked}
            isRelayEmail={onboardingData.is_relay_email}
            reason={onboardingData.onboarding_reason}
          />
          
          {/* Info about what they'll see after */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              {language === 'en' 
                ? 'After verification, you\'ll have full access to your dashboard with booking info, entry code, and more.'
                : 'Dopo la verifica avrai accesso completo alla dashboard con info prenotazione, codice ingresso e altro.'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Email Verification Banner - Show if not verified but skipped */}
        {onboardingData && !onboardingData.email_verified && onboardingDismissed && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-amber-600" />
                <p className="text-amber-800 text-sm">
                  {language === 'en' 
                    ? 'Verify your email to keep access to your bookings after checkout.'
                    : 'Verifica la tua email per mantenere l\'accesso alle prenotazioni dopo il checkout.'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => {
                  setOnboardingDismissed(false);
                  setShowOnboarding(true);
                }}
                data-testid="verify-email-banner-btn"
              >
                {language === 'en' ? 'Verify Now' : 'Verifica Ora'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Welcome Section */}
        <div className="mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-2">
              {language === 'en' ? 'Welcome' : 'Benvenuto'}
            </p>
            <h1 className="font-cinzel text-4xl md:text-5xl text-[#1A202C] mb-4">
              {language === 'en' ? 'Hello' : 'Ciao'}, {user.nome}!
            </h1>
            <p className="font-manrope text-[#4A5568]">
              {language === 'en' 
                ? 'Manage your stay and discover exclusive benefits'
                : 'Gestisci il tuo soggiorno e scopri i vantaggi esclusivi'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/account-settings">
              <Button 
                variant="outline" 
                size="sm" 
                className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                data-testid="dashboard-account-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                {language === 'en' ? 'Account Settings' : 'Impostazioni'}
              </Button>
            </Link>
            <PushNotificationToggle token={token} language={language} />
          </div>
        </div>

        {/* ===================== CODICE INGRESSO - CARD SEMPRE VISIBILE ===================== */}
        <div className="mb-8 bg-gradient-to-r from-[#1B4332] via-[#2D5A45] to-[#1B4332] rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-xl" data-testid="access-code-card">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }} />
          </div>
          
          <div className="relative z-10">
            {/* Accommodation Info Section */}
            {(unitName || validityStart) && (
              <div className="mb-6 pb-5 border-b border-white/20" data-testid="accommodation-info">
                {/* Unit Name - Prominent Display */}
                {unitName && (
                  <div className="text-center mb-3">
                    <p className="text-gray-300 text-xs uppercase tracking-widest mb-1">
                      {language === 'en' ? 'Assigned Accommodation' : 'Alloggio Assegnato'}
                    </p>
                    <h2 className="text-white font-cinzel text-2xl md:text-3xl font-semibold tracking-wide" data-testid="unit-name-display">
                      {language === 'en' && unitNameEn ? unitNameEn : unitName}
                    </h2>
                  </div>
                )}
                
                {/* Validity Period */}
                {validityStart && validityEnd && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-300" data-testid="validity-dates">
                    <Calendar className="w-4 h-4 text-[#C5A059]" />
                    <span>
                      {language === 'en' ? 'Valid from' : 'Valido dal'}{' '}
                      <span className="font-semibold text-white">
                        {new Date(validityStart).toLocaleDateString(language === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                      {' '}{language === 'en' ? 'to' : 'al'}{' '}
                      <span className="font-semibold text-white">
                        {new Date(validityEnd).toLocaleDateString(language === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Left side - Icon and Text */}
              <div className="flex items-center gap-4 text-white">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#C5A059] flex items-center justify-center shadow-xl">
                  <Key className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <div>
                  <p className="text-[#C5A059] font-cinzel text-xs md:text-sm tracking-widest uppercase mb-1">
                    {language === 'en' ? 'Entry Code' : 'Codice di Ingresso'}
                  </p>
                  <p className="text-sm md:text-base text-gray-200">
                    {codiceIngresso 
                      ? (language === 'en' 
                          ? 'Use this code to access your accommodation'
                          : 'Usa questo codice per accedere alla tua struttura')
                      : (language === 'en'
                          ? 'Your entry code will appear here'
                          : 'Il tuo codice di accesso apparirà qui')
                    }
                  </p>
                </div>
              </div>
              
              {/* Right side - Code Display or Unavailable State */}
              {codiceIngresso ? (
                <div 
                  className="bg-white/10 backdrop-blur-sm border-2 border-[#C5A059] rounded-2xl px-6 md:px-10 py-4 md:py-5 cursor-pointer hover:bg-white/20 transition-all group"
                  onClick={() => {
                    navigator.clipboard.writeText(codiceIngresso);
                    setCodeCopied(true);
                    toast.success(language === 'en' ? 'Code copied!' : 'Codice copiato!');
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  data-testid="entry-code-display"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-4xl md:text-5xl font-bold text-white tracking-[0.3em] font-mono" data-testid="entry-code-value">
                      {codiceIngresso}
                    </span>
                    <div className="p-2 rounded-lg bg-[#C5A059]/20 group-hover:bg-[#C5A059]/40 transition-colors">
                      {codeCopied ? (
                        <Check className="w-6 h-6 text-green-400" />
                      ) : (
                        <Copy className="w-6 h-6 text-[#C5A059]" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-center text-gray-300 mt-2">
                    {codeCopied 
                      ? (language === 'en' ? '✓ Copied!' : '✓ Copiato!') 
                      : (language === 'en' ? 'Tap to copy' : 'Tocca per copiare')}
                  </p>
                </div>
              ) : (
                <div 
                  className="bg-white/5 backdrop-blur-sm border-2 border-dashed border-gray-400/50 rounded-2xl px-6 md:px-10 py-4 md:py-5"
                  data-testid="entry-code-unavailable"
                >
                  <div className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-gray-300 mb-2" data-testid="entry-code-placeholder">
                      {language === 'en' ? 'Not yet available' : 'Non ancora disponibile'}
                    </p>
                    {/* Reason for unavailability */}
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      {codeUnavailableReason === 'checkin_pending' && (
                        <>
                          <ClipboardCheck className="w-4 h-4" />
                          <span>{language === 'en' ? 'Complete online check-in' : 'Completa il check-in online'}</span>
                        </>
                      )}
                      {codeUnavailableReason === 'unit_not_assigned' && (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>{language === 'en' ? 'Room assignment in progress' : 'Assegnazione alloggio in corso'}</span>
                        </>
                      )}
                      {codeUnavailableReason === 'preparing' && (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>{language === 'en' ? 'Code being prepared' : 'Codice in preparazione'}</span>
                        </>
                      )}
                      {codeUnavailableReason === 'no_booking' && (
                        <>
                          <AlertCircle className="w-4 h-4" />
                          <span>{language === 'en' ? 'No active booking' : 'Nessuna prenotazione attiva'}</span>
                        </>
                      )}
                      {codeUnavailableReason === 'error' && (
                        <>
                          <Phone className="w-4 h-4" />
                          <span>{language === 'en' ? 'Contact the property' : 'Contatta la struttura'}</span>
                        </>
                      )}
                      {!codeUnavailableReason && (
                        <>
                          <Clock className="w-4 h-4" />
                          <span>{language === 'en' ? 'Loading...' : 'Caricamento...'}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Action Buttons - Only when code is available */}
            {codiceIngresso && (
              <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap justify-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-[#C5A059] text-white hover:bg-[#C5A059] hover:text-white"
                  onClick={() => {
                    navigator.clipboard.writeText(codiceIngresso);
                    setCodeCopied(true);
                    toast.success(language === 'en' ? 'Code copied!' : 'Codice copiato!');
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  data-testid="copy-entry-code-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Copy Code' : 'Copia Codice'}
                </Button>
                <Link to="/house-rules">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-[#C5A059] text-white hover:bg-[#C5A059] hover:text-white"
                    data-testid="instructions-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Instructions' : 'Istruzioni'}
                  </Button>
                </Link>
              </div>
            )}
            
            {/* Instructions text */}
            {codiceIngresso && (
              <p className="text-center text-sm text-gray-300 mt-4">
                {language === 'en' 
                  ? 'Please enter this code on the keypad at the entrance of the property.'
                  : 'La invitiamo a digitare questo codice sulla tastiera posta all\'ingresso della struttura.'}
              </p>
            )}
            
            {/* Date Info */}
            {activeBooking && (activeBooking.data_arrivo || activeBooking.check_in) && (
              <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap justify-center gap-4 md:gap-8 text-sm text-gray-300">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#C5A059]" />
                  {new Date(activeBooking.data_arrivo || activeBooking.check_in).toLocaleDateString('it-IT')} - {new Date(activeBooking.data_partenza || activeBooking.check_out).toLocaleDateString('it-IT')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Booking Code Card */}
        <Card className="mb-8 bg-[#1A202C] text-white border-none overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C5A059]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-8 relative">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="font-manrope text-sm text-gray-400 mb-2">
                  {activeBooking ? 'Codice prenotazione attiva' : 'Il tuo codice utente'}
                </p>
                <div className="flex items-center gap-4">
                  <p className="font-cinzel text-3xl text-[#C5A059]" data-testid="booking-code">
                    {activeBooking?.codice_prenotazione || user.codice_prenotazione || '-'}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={copyCode}
                    className="text-gray-400 hover:text-[#C5A059]"
                    data-testid="copy-code-btn"
                  >
                    {codeCopied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
                <p className="font-manrope text-xs text-gray-500 mt-2">
                  {activeBooking 
                    ? `Soggiorno: ${new Date(activeBooking.data_arrivo).toLocaleDateString('it-IT')} - ${new Date(activeBooking.data_partenza).toLocaleDateString('it-IT')}`
                    : 'Usa questo codice per accedere rapidamente'
                  }
                </p>
              </div>
              <div className="text-right">
                <p className="font-manrope text-sm text-gray-400 mb-1">Punti Fedeltà</p>
                <p className="font-cinzel text-4xl text-[#C5A059]" data-testid="points-display">
                  {user.punti_fedelta || 0}
                </p>
                <p className="font-manrope text-xs text-gray-500">
                  {user.punti_fedelta >= 100 
                    ? '🎁 Hai diritto a una notte omaggio!' 
                    : `${100 - (user.punti_fedelta || 0)} punti alla prossima notte omaggio`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {[
            { to: '/checkin', icon: ClipboardCheck, label: 'Nuovo Check-in', desc: 'Effettua il check-in' },
            { to: '/events', icon: Calendar, label: 'Eventi', desc: 'Scopri gli eventi' },
            { to: '/structures', icon: MapPin, label: 'Territorio', desc: 'Esplora la zona' },
            { to: '/loyalty', icon: Gift, label: 'Punti Fedeltà', desc: 'Gestisci i punti' }
          ].map((action, index) => (
            <Link key={index} to={action.to}>
              <Card className="h-full card-hover border-[#E2E8F0] hover:border-[#C5A059] transition-all" data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-[#F9F9F7] flex items-center justify-center mb-4 rounded-sm">
                    <action.icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-cinzel text-lg text-[#1A202C] mb-1">{action.label}</h3>
                  <p className="font-manrope text-sm text-[#4A5568]">{action.desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Check-ins */}
        <Card className="border-[#E2E8F0]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-cinzel text-xl text-[#1A202C]">
              I Tuoi Check-in
            </CardTitle>
            <Link to="/checkin">
              <Button variant="outline" size="sm" className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white" data-testid="new-checkin-btn">
                Nuovo Check-in
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : checkins.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
                <p className="font-manrope text-[#4A5568] mb-4">
                  Non hai ancora effettuato check-in
                </p>
                <Link to="/checkin">
                  <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white" data-testid="first-checkin-btn">
                    Effettua il primo Check-in
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {checkins.slice(0, 5).map((checkin) => (
                  <div 
                    key={checkin.id}
                    className="flex items-center justify-between p-4 bg-[#F9F9F7] rounded-sm"
                    data-testid={`checkin-item-${checkin.id}`}
                  >
                    <div>
                      <p className="font-manrope font-medium text-[#1A202C]">
                        {new Date(checkin.data_arrivo).toLocaleDateString('it-IT', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })} - {new Date(checkin.data_partenza).toLocaleDateString('it-IT', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </p>
                      <p className="font-manrope text-sm text-[#4A5568]">
                        {checkin.num_ospiti} {checkin.num_ospiti === 1 ? 'ospite' : 'ospiti'}
                      </p>
                    </div>
                    {getStatusBadge(checkin.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Settings Card - Link to dedicated page */}
        <Card className="mt-8 border-[#C5A059]/30 bg-gradient-to-r from-[#F9F9F7] to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                  <Settings className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-cinzel text-lg text-[#1A202C]">
                    {language === 'en' ? 'Account Settings' : 'Impostazioni Account'}
                  </h3>
                  <p className="text-sm text-[#4A5568] font-manrope">
                    {language === 'en' ? 'Privacy, data management, delete account' : 'Privacy, gestione dati, elimina account'}
                  </p>
                </div>
              </div>
              <Link to="/account-settings">
                <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white" data-testid="go-to-account-settings">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
